import React, { useEffect, useState } from 'react';
import { Modal, UnstyledButton } from '@mantine/core';
import { Button } from '../../../../../components/Button';
import { formatMoney } from '../../../../../utils/money';
import { paymentMethodLabel } from './TransactionReceiptContent';
import type { ReceiptPrintJob } from './receipt-split.util';
import classes from './receipt-print-selection-modal.module.scss';

type Props = {
  opened: boolean;
  jobs: ReceiptPrintJob[];
  isPrinting: boolean;
  onClose: () => void;
  onPrint: (jobIds: string[]) => void;
};

function jobLabel(job: ReceiptPrintJob): string {
  if (job.title) return job.title;
  if (!job.receipt.isPartial) return 'קבלה מלאה';
  const payment = job.receipt.payments[0];
  return `${paymentMethodLabel(payment)} · ${formatMoney(payment.amount)} ₪`;
}

const ReceiptPrintSelectionModalComponent: React.FC<Props> = ({ opened, jobs, isPrinting, onClose, onPrint }) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!opened) setSelectedIds(new Set());
  }, [opened]);

  if (!opened) return null;

  const allSelected = jobs.length > 0 && jobs.every((job) => selectedIds.has(job.id));
  const toggle = (id: string) => {
    if (isPrinting) return;
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(jobs.map((job) => job.id)));

  return (
    <Modal opened={opened} onClose={onClose} title="בחירת קבלות להדפסה" size="sm" centered radius="md" dir="rtl" zIndex={1200} closeOnClickOutside={!isPrinting} closeOnEscape={!isPrinting} closeButtonProps={{ 'data-testid': 'receipt-print-selection-close' } as Record<string, string>}>
      <div className={classes.shell}>
        <UnstyledButton type="button" className={classes.selectAll} onClick={toggleAll} disabled={isPrinting} aria-pressed={allSelected} data-testid="receipt-print-selection-select-all">
          <span>בחר הכל</span>
          <span className={`${classes.checkbox} ${allSelected ? classes.checkboxSelected : ''}`}>{allSelected ? '✓' : ''}</span>
        </UnstyledButton>
        <div className={classes.list}>
          {jobs.map((job) => {
            const selected = selectedIds.has(job.id);
            return (
              <UnstyledButton
                key={job.id}
                type="button"
                className={`${classes.row} ${selected ? classes.rowSelected : ''}`}
                onClick={() => toggle(job.id)}
                disabled={isPrinting}
                aria-pressed={selected}
                data-testid={`receipt-print-selection-row-${job.id}`}
              >
                <span>{jobLabel(job)}</span>
                <span className={`${classes.checkbox} ${selected ? classes.checkboxSelected : ''}`}>{selected ? '✓' : ''}</span>
              </UnstyledButton>
            );
          })}
        </div>
        <div className={classes.actions}>
          <Button variant="cancel" fullWidth onClick={onClose} disabled={isPrinting} dataTestId="receipt-print-selection-cancel">ביטול</Button>
          <Button variant="confirm" fullWidth onClick={() => onPrint(Array.from(selectedIds))} disabled={isPrinting || selectedIds.size === 0} dataTestId="receipt-print-selection-confirm">
            {isPrinting ? 'מדפיס...' : 'הדפס'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const ReceiptPrintSelectionModal = React.memo(ReceiptPrintSelectionModalComponent);